export type ColorSchemeName =
  | 'green'
  | 'blue'
  | 'red'
  | 'purple'
  | 'dark-yellow'
  | 'teal'
  | 'orange'
  | 'cyan'
  | 'pink';

type Scale = 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900;

type Palette = { primary: Record<Scale, string> };

export const SURFACE_ACCENTS: Record<ColorSchemeName, string> = {
  green: '#2E3E3A',
  blue: '#1F2E4F',
  red: '#3E2E2E',
  purple: '#2A2040',
  'dark-yellow': '#3E3A2E',
  teal: '#1D4044',
  orange: '#652B19',
  cyan: '#065666',
  pink: '#521B41',
};

export const COLOR_SCHEMES: Record<ColorSchemeName, Palette> = {
  green: {
    primary: {
      50: '#f0fff4',
      100: '#c6f6d5',
      200: '#9ae6b4',
      300: '#ECFFEC',
      400: '#ADF2AF4D',
      500: '#ADF2AF',
      600: '#2f855a',
      700: '#276749',
      800: '#22543d',
      900: '#1c4532',
    },
  },
  blue: {
    primary: {
      50: '#ebf8ff',
      100: '#bee3f8',
      200: '#90cdf4',
      300: '#63b3ed',
      400: '#4299e1',
      500: '#3182ce',
      600: '#2b6cb0',
      700: '#2c5282',
      800: '#2a4365',
      900: '#1A365D',
    },
  },
  red: {
    primary: {
      50: '#FFF5F5',
      100: '#FED7D7',
      200: '#FEB2B2',
      300: '#FC8181',
      400: '#F56565',
      500: '#E53E3E',
      600: '#C53030',
      700: '#9B2C2C',
      800: '#822727',
      900: '#63171B',
    },
  },
  purple: {
    primary: {
      50: '#f7e9ff',
      100: '#ead0ff',
      200: '#d9b2ff',
      300: '#c495ff',
      400: '#b27dff',
      500: '#9f7aea',
      600: '#8b5cf6',
      700: '#7c3aed',
      800: '#6d28d9',
      900: '#5b21b6',
    },
  },
  'dark-yellow': {
    primary: {
      50: '#FFFBEB',
      100: '#FEF3C7',
      200: '#FDE68A',
      300: '#FCD34D',
      400: '#FBBF24',
      500: '#F59E0B',
      600: '#D97706',
      700: '#B45309',
      800: '#92400E',
      900: '#78350F',
    },
  },
  teal: {
    primary: {
      50: '#E6FFFA',
      100: '#B2F5EA',
      200: '#81E6D9',
      300: '#4FD1C5',
      400: '#38B2AC',
      500: '#319795',
      600: '#2C7A7B',
      700: '#285E61',
      800: '#234E52',
      900: '#1D4044',
    },
  },
  orange: {
    primary: {
      50: '#FFFAF0',
      100: '#FEEBC8',
      200: '#FBD38D',
      300: '#F6AD55',
      400: '#ED8936',
      500: '#DD6B20',
      600: '#C05621',
      700: '#9C4221',
      800: '#7B341E',
      900: '#652B19',
    },
  },
  cyan: {
    primary: {
      50: '#EDFDFD',
      100: '#C4F1F9',
      200: '#9DECF9',
      300: '#76E4F7',
      400: '#0BC5EA',
      500: '#00B5D8',
      600: '#00A3C4',
      700: '#0987A0',
      800: '#086F83',
      900: '#065666',
    },
  },
  pink: {
    primary: {
      50: '#FFF5F7',
      100: '#FED7E2',
      200: '#FBB6CE',
      300: '#F687B3',
      400: '#ED64A6',
      500: '#D53F8C',
      600: '#B83280',
      700: '#97266D',
      800: '#702459',
      900: '#521B41',
    },
  },
};

export const PRIMARY_TEXT_COLOR: Record<ColorSchemeName, string> = {
  green: '#202631',
  'dark-yellow': 'white',
  blue: 'white',
  red: 'white',
  purple: 'white',
  teal: 'white',
  orange: 'white',
  cyan: 'white',
  pink: 'white',
};

export function validateColorSchemeName(
  value: unknown,
  fallback: ColorSchemeName = 'green',
): ColorSchemeName {
  const v = typeof value === 'string' ? (value as string).trim().toLowerCase() : '';
  if (
    v === 'green' ||
    v === 'blue' ||
    v === 'red' ||
    v === 'purple' ||
    v === 'dark-yellow' ||
    v === 'teal' ||
    v === 'orange' ||
    v === 'cyan' ||
    v === 'pink'
  ) {
    return v as ColorSchemeName;
  }
  if (typeof window !== 'undefined') {
    console.warn(
      `[theme] Unknown COLOR_SCHEME "${String(value)}". Falling back to "${fallback}". ` +
        'Allowed: green | blue | red | purple | dark-yellow | teal | orange | cyan | pink.',
    );
  }
  return fallback;
}
